import {
  browserPolicyNameToBrowser,
  ospPolicyNameToOs,
  ospPolicyNames,
  policiesUpdatesConfigFields,
  zeroStateBrowserPatchTemplate,
  zeroStateTemplate,
} from 'client/util/Constants/OsPatchMgmtConstants';
import { clone } from 'lodash';
import { mapActions } from 'vuex';
import { notify } from 'client/util';
import ConfigurationGroupApi from 'client/api/v2/ConfigurationGroupApi';
import Constants, {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import PolicyApi from 'client/api/v2/PolicyApi';
import RecommendedPolicyGroupsData from 'client/util/Constants/RecommendedPolicyGroupsConstants';

const policyTemplateCollection = StoreModuleMap[ItemTypeConsts.policyTemplate];
const policyCollection = StoreModuleMap[ItemTypeConsts.policy];
const configurationModel = StoreModelMap[ItemTypeConsts.configuration];
const configurationGroupCollection = StoreModuleMap[ItemTypeConsts.configurationGroup];
const configurationGroupModel = StoreModelMap[ItemTypeConsts.configurationGroup];
const { error, success } = Constants;
const { ConfigurationGroupItemType, PolicyItemType } = ItemType;

const policyItemType = new PolicyItemType();
const configurationGroupItemType = new ConfigurationGroupItemType();

export default {
  data: () => ({
    ospPolicies: {},
  }),

  methods: {
    ...mapActions(policyTemplateCollection, {
      deleteFilter: 'deleteFilter',
      fetchPolicyTemplates: 'fetchCollection',
      setFilter: 'setFilter',
    }),

    ...mapActions(policyCollection, [
      'selectItems',
      'unselectAllItems',
    ]),
    ...mapActions(configurationGroupCollection, {
      getPolicyGroupByName: 'getPolicyGroupByName',
    }),
    ...mapActions(configurationGroupModel, {
      fetchPolicyGroup: 'fetchItem',
      setPolicyGroupField: 'setItemField',
      bulkCreateDefaultPolicyGroups: 'bulkCreateDefaultPolicyGroups',
    }),

    ...mapActions(configurationModel, [
      'bulkCreatePatchPolicies',
    ]),

    async tryBulkCreateOsPatchPolicies(params) {
      const learnMoreKB = KbArticleUrls.patchManagementLearnMore;

      try {
        await this.bulkCreatePatchPolicies(params);
        notify(success, `The default OS patch policies were created! <br/><a href=${learnMoreKB} rel="noopener noreferrer" target="_blank">Learn more</a>`);
      } catch (e) {
        this.handleError(e, policyItemType.labelPlural);
      }
    },

    async tryBulkCreatePolicyGroups() {
      try {
        await this.bulkCreateDefaultPolicyGroups(RecommendedPolicyGroupsData);
        notify(success, `${RecommendedPolicyGroupsData.length} Ring Groups Successfully Created!`);
      } catch (e) {
        this.handleError(e, configurationGroupItemType.labelPlural);
      }
    },

    async tryBindPoliciesToPolicyGroups() {
      try {
        const policyGroups = await ConfigurationGroupApi.fetch(0, 50);
        this.ospPolicies = await this.fetchOspPolicies();
        const bindingTasks = [];

        RecommendedPolicyGroupsData.forEach(recommendedPolicy => {
          // Sets the policy group
          const group = this
            .getPolicyGroupByName(policyGroups.results, recommendedPolicy.name);

          // Sets the policies to bind
          const policiesToSelect = this.getIdsToBind(recommendedPolicy.name);

          bindingTasks.push(...policiesToSelect.map(policy => {
            const parent = { id: group.id, type: configurationGroupItemType.type };
            const target = { id: policy.id, type: policyItemType.type };
            return DirectoryGroupingApi.bind(parent, 'add', target);
          }));
        });

        await Promise.all(bindingTasks);
        notify(success, 'The default policies were bound to the default policy groups!');
      } catch (e) {
        notify(error, 'Unable to bind the patch policy to a policy group! Delete the default policy groups and try again later.');
      }
    },

    async createDefaultPoliciesAndPolicyGroups(policiesConfigFields, templates, os = '') {
      const params = { policiesConfigFields, templates };
      params.zeroStateData = zeroStateTemplate;
      // if it's OS specific it means that only the policies need to be created.
      const isOsSpecific = os !== '';
      params.suffix = ' Ring';

      await this.tryBulkCreateOsPatchPolicies(params);

      if (!isOsSpecific) {
        await this.tryBulkCreatePolicyGroups();
        await this.tryBindPoliciesToPolicyGroups();
      }
    },

    getIdsToBind(groupName) {
      return this.ospPolicies.results.filter(policy => policy.name.includes(groupName));
    },

    handleError(e, resource) {
      // If it is a 409 error, we do not want to show a toast because the conflicting resource
      // will still be used, so it does not disrupt the functionality.
      const alreadyExists = 409;
      if (e.status !== alreadyExists) {
        notify(error, `An error occurred when creating the default ${resource}. Try again later.`);
      }
    },

    fetchOspPolicies() {
      const filteredNames = clone(ospPolicyNames);
      return PolicyApi.findPoliciesByName(filteredNames);
    },

    /**
     * Gets and sets the config fields for the relevant delay days fields in the policy template.
     * This is needed because the configFieldIDs change between environments.
     */
    async getConfigFields() {
      const policiesConfigFields = {};

      this.setFilter({
        filter: {
          name: 'name',
          operator: 'in',
          value: ospPolicyNames.join('|'),
        },
      });

      await this.fetchPolicyTemplates();

      if (this.policyTemplates.length === 0) return false;

      Promise.all(this.policyTemplates.map(async (template) => {
        const os = ospPolicyNameToOs[template.name];
        if (!os) {
          return;
        }
        const policyItem = await this.fetchItem(template.id);
        policiesConfigFields[os] = {};

        Object.entries(policiesUpdatesConfigFields[os]).forEach(([updateType, fields]) => {
          policiesConfigFields[os][updateType] = {};

          Object.entries(fields).forEach(([field, fieldName]) => {
            const configField = policyItem.template.configFields
              .find(({ name }) => name === fieldName);
            if (configField !== undefined) {
              policiesConfigFields[os][updateType][field] = configField.id;
            }
          });
        });
      }));
      return policiesConfigFields;
    },

    getPolicyGroupByName(policyGroups, name) {
      return Object.values(policyGroups).find(item => item.name === name);
    },

    // Browser Patch Management
    fetchBrowserPatchPolicies() {
      return PolicyApi.findPoliciesByName(['chrome_universal']);
    },

    async getConfigFieldsBrowserPatch(policyNames) {
      const policiesConfigFields = [];

      this.setFilter({
        filter: {
          name: 'name',
          operator: 'in',
          value: policyNames.join('|'),
        },
      });

      await this.fetchPolicyTemplates();

      if (this.policyTemplates.length === 0) return false;

      Promise.all(this.policyTemplates.map(async (template) => {
        const browser = browserPolicyNameToBrowser[template.name];
        if (!browser) return;
        // Get configField IDs
        const policyItem = await this.fetchItem(template.id);
        policiesConfigFields[browser] = {};

        // This forEach associates the configField name
        // with the corresponding configField ID according the environment
        Object.entries(zeroStateBrowserPatchTemplate[browser]).forEach((fields) => {
          Object.entries(fields[1]).forEach(([field]) => {
            const configField = policyItem.template.configFields
              .find(({ name }) => name === field);
            if (configField !== undefined) {
              policiesConfigFields[browser][field] = configField.id;
            }
          });
        });
      }));
      return policiesConfigFields;
    },

    async createDefaultPoliciesBrowserPatch(policiesConfigFields, templates) {
      const params = { policiesConfigFields, templates };
      params.zeroStateData = zeroStateBrowserPatchTemplate;
      params.browserPatchMgmt = true;
      await this.tryBulkCreateBrowserPatchPolicies(params);
    },

    async tryBulkCreateBrowserPatchPolicies(params) {
      try {
        await this.bulkCreatePatchPolicies(params);
        notify(success, 'The default Browser patch policies were created!');
      } catch (e) {
        this.handleError(e, policyItemType.labelPlural);
      }
    },
  },
};
